import React, { useEffect, useState } from 'react';
import { Modal } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import axiosInstance from '../config';
import qs from 'querystring';
import { toast } from 'react-toastify';
import AgentUtils from '../agentutils';

const useStyles = makeStyles(() => ({
  Modal: {
    width: '900px',
    maxWidth: '100%',
    margin: 'auto',
    backgroundColor: '#fff',
    borderRadius: '4px',
    outline: 'none',
  },
  // The scrolling container, not the card. A fixed height with overflowY:'scroll' pinned the dialog
  // to 80% of the viewport and drew a scrollbar rail even when the content fitted (two categories on
  // a member - the common case). This fills the viewport, lets the card size to its content, and only
  // scrolls when the content is actually taller. margin:'auto' on the card centres it when there is
  // room and collapses to 0 when there is not, so a tall dialog starts at the top instead of being
  // clipped above the fold.
  modalStyle: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    padding: '2rem 1rem',
    overflowY: 'auto',
  },
  tableBody: {
    '& td': {
      padding: '0.25rem 1rem  !important',
    },
  },
}));

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? NaN : n;
};

const isEmpty = (value) => value === null || value === undefined;

/** Same words as the "Edit %" modal, so one flag reads the same on both screens. */
const convertType = (type) => {
  if (type === 'WE_REQUIRE') return 'We Require';
  return 'I Require';
};

/**
 * Which fields this level's backend handler actually persists.
 * Mirrors AgentSettingService.saveAgentShare (@a28dev, wega-seamless):
 *   VENDOR -> createVendorShare(agent, minShare, maxShare)   : min, max
 *   SENIOR -> createSeniorShare(agent, up, min, max, type)   : up, min, max, type
 *   MASTER -> createMasterShare(agent, up, min, max, type)   : up, min, max, type
 *   AGENT  -> createAgentShare(agent, masterShare, share)    : up, share
 * Anything not listed for a level is ignored server-side, so no input is offered for it.
 */
const LEVEL_WRITES = {
  VENDOR: ['min', 'max'],
  SENIOR: ['up', 'min', 'max', 'type'],
  MASTER: ['up', 'min', 'max', 'type'],
  AGENT: ['up', 'share'],
};

// The three states of the games-access control, as its own buttons read them.
const ACCESS_LABEL = {
  ENABLE: 'Enabled',
  DISABLE: 'Switched off',
  NOT_SET: 'Not set, inherits',
};

const LEVEL_WRITES_LABEL = {
  VENDOR: 'Min / Max only',
  SENIOR: 'Require flag, upline share, Min / Max',
  MASTER: 'Require flag, upline share, Min / Max',
  AGENT: 'Require flag, upline share, own share',
};

/**
 * The games setting for one agent.
 *
 * <p>{@code commissionOnly} is how the Member menu opens it: there, the screen sets the commission
 * for a category and nothing else - the same thing the sports "Setting" link sets. The share rate
 * stays on this screen for the member table's own link.</p>
 */
const WegaStake = ({ wegaStakeModal, setWegaStakeModal, id, username, commissionOnly = false }) => {
  const classes = useStyles();
  // The same split the sports Setting screen makes: an 8-character username is an agent, anything
  // longer is a member (Player). A member is served by /agent/members, and has no games share rate.
  const isMember = !!username && username.length > 8;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shareRows, setShareRows] = useState([]);
  const [shareError, setShareError] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  // One form per Wega category: the section shows a row per category and posts them together,
  // the same way the stake section below does.
  const [shareForm, setShareForm] = useState({});
  // Whether this line may play WeGa at all - the company's own control, and it is the same control on both
  // tables against a different row: an agent's flag lives on /agent/agents/{id}/wegaAccess and a member's on
  // /agent/members/{id}/wegaAccess, because a member is a Player and not an Agent. Either way it serves
  // nobody but the company and its sub-users. The member is the nearest level of the walk, so enabling one
  // member reopens that player under an agent whose own line is off.
  const [access, setAccess] = useState(null);
  const [accessSaving, setAccessSaving] = useState(false);
  const accessUrl = `${isMember ? '/agent/members' : '/agent/agents'}/${id}/wegaAccess`;
  const canSetAccess = AgentUtils.isCompanyOrCompanySub(sessionStorage.getItem('me'));

  const role = shareRows.length ? shareRows[0].role : null;
  const writes = LEVEL_WRITES[role] || [];
  const can = (field) => writes.indexOf(field) !== -1;

  const setShareField = (code, field) => (e) =>
    setShareForm((prev) => ({ ...prev, [code]: { ...prev[code], [field]: e.target.value } }));

  // Columns the row can carry. A writable field always gets a column, even when the
  // stored value is currently null - otherwise the value could never be set.
  const firstRow = shareRows.length ? shareRows[0] : null;
  const showUpBand = firstRow ? !isEmpty(firstRow.upMin) || !isEmpty(firstRow.upMax) : false;
  const showUplineRate = firstRow ? !isEmpty(firstRow.upShare) || can('up') : false;
  const showOwnBand = firstRow
    ? !isEmpty(firstRow.min) || !isEmpty(firstRow.max) || can('min') || can('max')
    : false;
  const showOwnRate = firstRow ? !isEmpty(firstRow.share) || can('share') : false;

  // The catch-all category an agent holds until a category is separated out. The screen calls it
  // "All Games" whatever the stored name says; the code is what the API keys on.
  const categoryLabel = (row) =>
    row.categoryCode === 'OTHERS' ? 'All Games' : row.categoryName || row.categoryCode;

  const handleClose = () => {
    setWegaStakeModal(false);
  };

  const loadShare = () => {
    // A member has no games share rate: the backend exposes no /agent/members share endpoint, so
    // this call must never fire for one.
    if (!id || isMember) return;
    setShareLoading(true);
    axiosInstance
      .get(`/agent/agents/${id}/wegaShareSetting`)
      .then((res) => {
        // The catch-all ("All Games", code OTHERS) is hidden from this panel by request: the games
        // price sits on the separated categories now, and the catch-all is a fallback nobody should
        // edit by hand. Filtering here removes it from the table AND from the payload below, which is
        // built from `shareRows` - so no OTHERS row is posted and the stored value is left untouched.
        const data = (res.data || []).filter((row) => row.categoryCode !== 'OTHERS');
        setShareRows(data);
        setShareError(!data.length);
        const next = {};
        data.forEach((row) => {
          next[row.categoryCode] = {
            up: isEmpty(row.up) ? '' : String(row.up),
            min: isEmpty(row.min) ? '' : String(row.min),
            max: isEmpty(row.max) ? '' : String(row.max),
            share: isEmpty(row.share) ? '' : String(row.share),
            // Never post an empty flag over an existing one: the senior/master handler
            // defaults a null requireType to I_REQUIRE, which would silently flip a
            // WE_REQUIRE row.
            type: row.type || 'I_REQUIRE',
          };
        });
        setShareForm(next);
      })
      .catch(() => {
        setShareRows([]);
        setShareError(true);
      })
      .then(() => setShareLoading(false));
  };

  const loadStake = () => {
    if (!id) return;
    setLoading(true);
    let url = '/agent/agents';
    if (isMember) url = '/agent/members';
    axiosInstance
      .get(`${url}/${id}/wegaStakeSetting`)
      .then((res) => {
        // Same as the share panel: the catch-all ("All Games") is hidden - for an agent and, now that
        // every member is created with a row per Wega category, for a member too. The commission
        // payload is built from `rows`, so a hidden row is never posted: the backend writes only the
        // rows it is sent and OTHERS keeps whatever it holds.
        // The fallback is not dead weight. A member created before the per-category rows existed, or
        // one whose agent prices no separated category, holds the catch-all and nothing else - hiding
        // it would leave an empty screen with Save disabled and no way to price that member at all.
        const categoryRows = (res.data || []).filter((row) => row.categoryCode !== 'OTHERS');
        setRows(categoryRows.length ? categoryRows : (res.data || []));
      })
      .catch((err) => {
        console.log(err);
      })
      .then(() => setLoading(false));
  };

  const loadAccess = () => {
    if (!id || !canSetAccess) return;
    setAccess(null);
    axiosInstance
      .get(accessUrl)
      .then((res) => setAccess(res.data))
      .catch((err) => console.log(err));
  };

  // Written on click, on its own call and its own toast, rather than folded into the share Save below:
  // this screen already has two save paths, and a third entangled with them is how a half-saved row
  // happens. The answer is the level as it stands after the write, so the badge cannot go stale.
  const setAccessTo = (value) => {
    if (accessSaving || !canSetAccess) return;
    setAccessSaving(true);
    axiosInstance
      .post(accessUrl, { access: value })
      .then((res) => {
        setAccess(res.data);
        toast.success(`Games access: ${ACCESS_LABEL[value]}`, {
          position: 'top-right',
          autoClose: 3000,
        });
      })
      .catch((err) => {
        console.log(err);
        toast.error('Unable to set games access.', {
          position: 'top-right',
          autoClose: 5000,
        });
      })
      .finally(() => setAccessSaving(false));
  };

  useEffect(() => {
    if (wegaStakeModal) {
      loadShare();
      loadStake();
      loadAccess();
    }
  }, [wegaStakeModal, id]);

  const handleEditShare = (e) => {
    e.preventDefault();
    if (!writes.length) return false;

    const rowsPayload = [];
    for (const row of shareRows) {
      const values = shareForm[row.categoryCode] || {};
      const entry = { categoryCode: row.categoryCode };
      if (can('up') && values.up !== '' && values.up !== undefined) entry.up = values.up;
      if (can('min') && values.min !== '' && values.min !== undefined) entry.min = values.min;
      if (can('max') && values.max !== '' && values.max !== undefined) entry.max = values.max;
      if (can('share') && values.share !== '' && values.share !== undefined) entry.share = values.share;
      if (can('type') && values.type) entry.type = values.type;

      if (
        entry.min !== undefined &&
        entry.max !== undefined &&
        toNumber(entry.min) > toNumber(entry.max)
      ) {
        toast.error('Error, Max value must not be bigger than min value [ > ' + entry.min + ' ]', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
        return false;
      }
      rowsPayload.push(entry);
    }

    setShareSaving(true);
    axiosInstance
      .post(`/agent/agents/${id}/wegaShareSetting`, { rows: rowsPayload })
      .then(() => {
        toast.success('Games share rate saved!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
        loadShare();
        loadStake();
      })
      .catch((err) => {
        console.log(err);
        if (err.response && err.response.status === 401) {
          window.location.href = '/';
        } else {
          toast.error('Error, please reload or try again', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      })
      .then(() => setShareSaving(false));
  };

  const handleEditStake = (e) => {
    e.preventDefault();
    setLoading(true);

    let invalid = false;
    rows.forEach((row) => {
      const commission = toNumber(row.commission);
      if (Number.isNaN(commission)) invalid = true;
      if (commission !== null && (commission < 0 || commission > 100)) invalid = true;
      if (
        commission !== null &&
        row.uplineCommission !== null &&
        commission > Number(row.uplineCommission)
      )
        invalid = true;
    });

    if (invalid) {
      toast.error('Error, please check your values against the upline limits', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
      setLoading(false);
      return false;
    }

    // The endpoint takes {rows:[...]} - a bare array fails to bind and answers 400 with no body.
    const payload = {
      rows: rows.map((row) => ({
        categoryCode: row.categoryCode,
        commission: toNumber(row.commission),
      })),
    };

    let url = '/agent/agents';
    if (isMember) url = '/agent/members';
    axiosInstance
      .post(`${url}/${id}/wegaStakeSetting`, payload)
      .then(() => {
        toast.success('Games commission saved!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
        loadStake();
      })
      .catch((err) => {
        console.log(err);
        if (err.response && err.response.status === 401) {
          window.location.href = '/';
        } else {
          toast.error('Error, please reload or try again', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      })
      .then(() => setLoading(false));
  };

  const onRowChange = (index, field) => (e) => {
    const value = e.target.value;
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  return (
    <Modal open={wegaStakeModal} onClose={handleClose}>
      <div className={classes.modalStyle}>
        <div className={classes.Modal}>
          <div className="bg-primary text-light p-2 d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Games Setting {username}</h5>
            <button type="button" className="btn btn-sm btn-link text-light" onClick={handleClose}>
              Close
            </button>
          </div>

          <div className="p-3">
            {/* Whether this line may play WeGa at all. Company-only, and only on an agent row. The rest of
                this screen stays reachable whatever the flag says: the share rate below has to be priced
                before games are switched on (Andrew, 2026-10-04). */}
            {canSetAccess ? (
              <div className="border rounded p-2 mb-3">
                <div className="d-flex align-items-center mb-1">
                  <span className="font-weight-bold text-uppercase mr-2">Games access</span>
                  <span
                    className={`badge ${
                      access && access.effective ? 'badge-success' : 'badge-secondary'
                    }`}
                  >
                    {!access ? '...' : access.effective ? 'Playing' : 'Switched off'}
                  </span>
                </div>
                {!access ? (
                  <p className="text-muted mb-0">Loading games access&hellip;</p>
                ) : (
                  <>
                    <div className="btn-group btn-group-sm mb-1" role="group">
                      <button
                        type="button"
                        className={`btn ${
                          access.access === 'NOT_SET' ? 'btn-secondary' : 'btn-outline-secondary'
                        }`}
                        disabled={accessSaving || access.access === 'NOT_SET'}
                        onClick={() => setAccessTo('NOT_SET')}
                      >
                        Not set
                      </button>
                      <button
                        type="button"
                        className={`btn ${
                          access.access === 'ENABLE' ? 'btn-success' : 'btn-outline-success'
                        }`}
                        disabled={accessSaving || access.access === 'ENABLE'}
                        onClick={() => setAccessTo('ENABLE')}
                      >
                        Enable
                      </button>
                      <button
                        type="button"
                        className={`btn ${
                          access.access === 'DISABLE' ? 'btn-danger' : 'btn-outline-danger'
                        }`}
                        disabled={accessSaving || access.access === 'DISABLE'}
                        onClick={() => setAccessTo('DISABLE')}
                      >
                        Disable
                      </button>
                    </div>
                    <small className="text-muted d-block">
                      {access.inherited
                        ? access.decidedBy
                          ? `Effective: ${
                              access.effective ? 'enabled' : 'disabled'
                            } - inherited from ${access.decidedBy}`
                          : `Effective: ${
                              access.effective ? 'enabled' : 'disabled'
                            } - no level has set this, so the platform default applies`
                        : `Effective: ${access.effective ? 'enabled' : 'disabled'} at this level`}
                      {access.access === 'NOT_SET' ? ' (this level has no setting of its own)' : ''}
                    </small>
                  </>
                )}
              </div>
            ) : null}

            {/* The share section is the member table's own link. The Member menu opens this screen
                commission-only, to match the sports "Setting" link beside it. */}
            {commissionOnly ? null : (
              <>
            {/* ----------------------------------------------------------------
                Share setting: one row per agent, shared by sports and games.
               ---------------------------------------------------------------- */}
            <div className="d-flex align-items-center mb-1">
              <span className="font-weight-bold text-uppercase mr-2">Share rate</span>
              <span className="badge badge-success mr-2">Games only</span>
              {role ? (
                <small className="text-muted">
                  {role} level &mdash; this endpoint writes: {LEVEL_WRITES_LABEL[role]}
                </small>
              ) : null}
            </div>

            {shareLoading ? (
              <p className="text-muted mb-0">Loading share setting&hellip;</p>
            ) : shareError || !shareRows.length ? (
              <p className="text-muted mb-0">
                No games share rate is listed for this level, or it could not be loaded. A SPORT
                category is priced from the sports share row and is not edited here.
              </p>
            ) : (
              <>
                <div className="table-responsive-sm">
                  <table className="table table-sm table-bordered">
                    <thead className="text-center thead-dark">
                      <tr>
                        <th rowSpan="2" className="thead-dark">
                          Market Type
                        </th>
                        {showUpBand ? (
                          <th colSpan="2" className="thead-dark">
                            Share Rate Limit
                          </th>
                        ) : null}
                        {showUplineRate ? (
                          <th rowSpan="2" className="thead-dark">
                            {firstRow.uprole} Share Rate
                          </th>
                        ) : null}
                        {showOwnBand ? (
                          <th colSpan="2" className="thead-dark">
                            {firstRow.role} Share Rate Limit
                          </th>
                        ) : null}
                        {showOwnRate ? (
                          <th rowSpan="2" className="thead-dark">
                            {firstRow.role} Share Rate
                          </th>
                        ) : null}
                      </tr>
                      {showUpBand || showOwnBand ? (
                        <tr>
                          {showUpBand ? (
                            <>
                              <th className="text-center thead-dark">Min (%)</th>
                              <th className="text-center thead-dark">Max (%)</th>
                            </>
                          ) : null}
                          {showOwnBand ? (
                            <>
                              <th className="text-center thead-dark">Min (%)</th>
                              <th className="text-center thead-dark">Max (%)</th>
                            </>
                          ) : null}
                        </tr>
                      ) : null}
                    </thead>
                    <tbody className={classes.tableBody}>
                      {shareRows.map((row) => {
                        const form = shareForm[row.categoryCode] || {};
                        return (
                          <tr key={row.categoryCode}>
                            <td className="text-dark text-center">
                              {categoryLabel(row)}
                              {row.separated ? null : (
                                <small className="text-muted d-block">inherited</small>
                              )}
                            </td>

                            {/* The upline's band - context from above, never editable downwards. */}
                            {showUpBand ? (
                              <>
                                <td className="text-dark text-center">{`${row.upMin}`}</td>
                                <td className="text-dark text-center">{`${row.upMax}`}</td>
                              </>
                            ) : null}

                            {/* The upline's rate. Levels that take an upline share get the input,
                                and the levels that carry the flag get the selector beside it. */}
                            {showUplineRate ? (
                              <td className="text-dark text-center">
                                {row.uprole === 'COMPANY' ? (
                                  <span>
                                    {row.upType ? convertType(row.upType) + ' ' : null}
                                    {`${row.upShare || ''}`}
                                  </span>
                                ) : can('up') ? (
                                  <>
                                    {can('type') ? (
                                      <select
                                        id={`type-${row.categoryCode}`}
                                        value={form.type}
                                        onChange={setShareField(row.categoryCode, 'type')}
                                      >
                                        <option value="I_REQUIRE">I Require</option>
                                        <option value="WE_REQUIRE">We Require</option>
                                      </select>
                                    ) : null}
                                    &nbsp;
                                    <input
                                      id={`upShare-${row.categoryCode}`}
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max="100"
                                      value={form.up}
                                      onChange={setShareField(row.categoryCode, 'up')}
                                    />
                                  </>
                                ) : (
                                  <span>{`${row.upShare || ''}`}</span>
                                )}
                              </td>
                            ) : null}

                            {/* This agent's own band. */}
                            {showOwnBand ? (
                              <>
                                <td className="text-dark text-center">
                                  {can('min') ? (
                                    <input
                                      id={`minShare-${row.categoryCode}`}
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max="100"
                                      value={form.min}
                                      onChange={setShareField(row.categoryCode, 'min')}
                                    />
                                  ) : (
                                    <span>{`${row.min}`}</span>
                                  )}
                                </td>
                                <td className="text-dark text-center">
                                  {can('max') ? (
                                    <input
                                      id={`maxShare-${row.categoryCode}`}
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max="100"
                                      value={form.max}
                                      onChange={setShareField(row.categoryCode, 'max')}
                                    />
                                  ) : (
                                    <span>{`${row.max}`}</span>
                                  )}
                                </td>
                              </>
                            ) : null}

                            {/* This agent's own rate. Only the AGENT handler writes it; above that
                                the split takes the upline's upline_share instead. */}
                            {showOwnRate ? (
                              <td className="text-dark text-center">
                                {can('share') ? (
                                  <input
                                    id={`share-${row.categoryCode}`}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={form.share}
                                    onChange={setShareField(row.categoryCode, 'share')}
                                  />
                                ) : (
                                  <span>{`${row.share}`}</span>
                                )}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <small className="text-muted d-block mb-2">
                  The games engine owns this rate. It is read by every bet in a Wega category, and it
                  is separate from the sports share row &mdash; so editing here no longer reprices
                  sports. New bets only; existing bets keep the figures they were stamped with.
                </small>
                <div className="text-right mb-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={shareSaving || !writes.length}
                    onClick={handleEditShare}
                  >
                    {shareSaving ? 'Saving...' : 'Save share rate'}
                  </button>
                </div>
              </>
            )}

            <hr />
              </>
            )}

            {/* ----------------------------------------------------------------
                Games commission: one row per games category, games only.
                The stake column is gone - a level's share rate is its percentage and no settlement
                path reads a games stake. The database column stays; nothing writes it from here.
               ---------------------------------------------------------------- */}
            <div className="d-flex align-items-center mb-1">
              <span className="font-weight-bold text-uppercase mr-2">
                Games commission
              </span>
              <span className="badge badge-info mr-2">Games only</span>
              <small className="text-muted">one row per games category</small>
            </div>

            <form onSubmit={handleEditStake}>
              <div className="table-responsive-sm">
                <table className="table table-sm table-bordered">
                  <thead className="text-center thead-dark">
                    <tr>
                      <th className="thead-dark">Category</th>
                      <th className="thead-dark">Commission</th>
                      <th className="thead-dark">Upline commission</th>
                      <th className="thead-dark">Downline commission</th>
                    </tr>
                  </thead>
                  <tbody className={classes.tableBody}>
                    {rows.length === 0 ? (
                      <tr className="text-center">
                        <td colSpan="4" className="text-muted">
                          {loading
                            ? 'Loading...'
                            : isMember
                              ? 'No games category is open for this member yet.'
                              : 'No games category is open for this agent yet.'}
                        </td>
                      </tr>
                    ) : (
                      rows.map((row, index) => (
                        <tr className="text-center" key={row.categoryCode || index}>
                          <td className="text-left font-weight-bold">
                            {categoryLabel(row)}
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.0001"
                              min="0"
                              max="100"
                              value={isEmpty(row.commission) ? '' : row.commission}
                              onChange={onRowChange(index, 'commission')}
                            />
                          </td>
                          <td>{isEmpty(row.uplineCommission) ? '-' : row.uplineCommission}</td>
                          <td>{isEmpty(row.downlineCommission) ? '-' : row.downlineCommission}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <small className="text-muted d-block mb-2">
                Games only &mdash; this prices the games tree and does not move the sports figures.
                The commission cannot exceed the upline limit shown.
              </small>
              <div className="text-right">
                <button type="button" className="btn btn-link" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={loading || rows.length === 0}
                >
                  {loading ? 'Saving...' : 'Save commission'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WegaStake;
